import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@/modules/auth'
import { queryClient } from '@/core/config/queryClient'
import {
  chatMessagePreviewLine,
  TActiveConversationFocus,
  TChatConversation,
  TChatMessage,
  TChatUserTypingPayload,
} from '../types'
import { chatKeys } from '../api/queryKeys'
import { showChatIncomingToast } from '../utils/showChatIncomingToast'
import {
  createEchoClient,
  disconnectEchoClient,
  getEchoClient,
} from '@/core/realtime/echo'
import { ensureSanctumCsrfCookie } from '@/core/config/axios/sanctum'

let sharedAudioContext: AudioContext | null = null

const unlockChatAudio = () => {
  if (sharedAudioContext) return
  try {
    sharedAudioContext = new AudioContext()
    if (sharedAudioContext.state === 'suspended') {
      void sharedAudioContext.resume()
    }
  } catch {
    sharedAudioContext = null
  }
}

const playNotificationChime = (ctx: AudioContext) => {
  const now = ctx.currentTime
  const duration = 0.42

  const master = ctx.createGain()
  master.connect(ctx.destination)
  master.gain.setValueAtTime(0, now)
  master.gain.linearRampToValueAtTime(0.14, now + 0.045)
  master.gain.linearRampToValueAtTime(0.09, now + 0.16)
  master.gain.linearRampToValueAtTime(0.0001, now + duration)

  const layers: Array<{ freq: number; type: OscillatorType; level: number }> = [
    { freq: 523.25, type: 'sine', level: 0.55 },
    { freq: 659.25, type: 'sine', level: 0.45 },
    { freq: 1046.5, type: 'sine', level: 0.12 },
  ]

  for (const { freq, type, level } of layers) {
    const osc = ctx.createOscillator()
    osc.type = type
    osc.frequency.setValueAtTime(freq, now)

    const g = ctx.createGain()
    g.gain.value = level

    osc.connect(g)
    g.connect(master)
    osc.start(now)
    osc.stop(now + duration)
  }
}

const playIncomingMessageSound = () => {
  try {
    const ctx = sharedAudioContext ?? new AudioContext()
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => playNotificationChime(ctx))
    } else {
      playNotificationChime(ctx)
    }
  } catch {
    // browser autoplay restrictions
  }
}

const sortConversation = (messages: TChatMessage[]) =>
  [...messages].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  )

const sortConversationsByLastMessage = (rows: TChatConversation[]) =>
  [...rows].sort((a, b) => {
    const aTs = a.last_message_at ? new Date(a.last_message_at).getTime() : 0
    const bTs = b.last_message_at ? new Date(b.last_message_at).getTime() : 0
    return bTs - aTs
  })

export const useChatStore = defineStore('chat', () => {
  const auth = useAuthStore()

  const listDrawerVisible = ref(false)
  const conversationDrawerVisible = ref(false)
  const activeConversationFocus = ref<TActiveConversationFocus | null>(null)

  const conversations = ref<TChatConversation[]>([])
  const messagesByThread = ref<Record<string, TChatMessage[]>>({})
  const onlineUlids = ref<Record<string, true>>({})

  const incomingTypingPeerUlid = ref<string | null>(null)
  const incomingTypingPeerName = ref<string | null>(null)
  const incomingTypingChatUlid = ref<string | null>(null)
  let incomingTypingTimer: ReturnType<typeof setTimeout> | null = null

  const isInitialized = ref(false)
  /** Nombres de canal Echo registrados en esta sesión (p. ej. logout puede limpiar auth antes de reset). */
  const presenceChannelJoined = ref<string | null>(null)
  const privateUserChannelUlid = ref<string | null>(null)

  const meUlid = computed(() => auth.user?.ulid ?? '')
  const totalUnreadCount = computed(() =>
    conversations.value.reduce((sum, c) => sum + (c.unread_count ?? 0), 0),
  )
  const hasPendingMessages = computed(() => totalUnreadCount.value > 0)

  const activeChatUlid = computed(
    () => activeConversationFocus.value?.chat_ulid ?? null,
  )

  const activePeerUlid = computed(() => {
    const f = activeConversationFocus.value
    if (!f) return null
    if (f.type === 'direct') return f.peer_ulid ?? null
    return null
  })

  const activeIsGroup = computed(
    () => activeConversationFocus.value?.type === 'group',
  )

  const activeMessages = computed(() => {
    const f = activeConversationFocus.value
    if (!f) return []
    if (f.chat_ulid) return messagesByThread.value[f.chat_ulid] ?? []
    if (f.peer_ulid) return messagesByThread.value[f.peer_ulid] ?? []
    return []
  })

  const showPeerIsTyping = computed(() => {
    const f = activeConversationFocus.value
    if (!f || !incomingTypingPeerName.value) return false
    if (f.type === 'group') {
      return (
        !!f.chat_ulid &&
        incomingTypingChatUlid.value === f.chat_ulid &&
        !!incomingTypingPeerUlid.value &&
        incomingTypingPeerUlid.value !== meUlid.value
      )
    }
    return (
      !!f.peer_ulid &&
      incomingTypingPeerUlid.value === f.peer_ulid &&
      incomingTypingPeerUlid.value !== meUlid.value
    )
  })

  const peerTypingDisplayName = computed(
    () => incomingTypingPeerName.value ?? '',
  )

  const clearIncomingTyping = () => {
    if (incomingTypingTimer) {
      clearTimeout(incomingTypingTimer)
      incomingTypingTimer = null
    }
    incomingTypingPeerUlid.value = null
    incomingTypingPeerName.value = null
    incomingTypingChatUlid.value = null
  }

  const applyIncomingTypingPayload = (payload: TChatUserTypingPayload) => {
    const f = activeConversationFocus.value
    if (!f || !payload?.peer_ulid) return

    if (f.type === 'group') {
      if (!f.chat_ulid || payload.chat_ulid !== f.chat_ulid) return
    } else {
      if (payload.peer_ulid !== f.peer_ulid) return
    }

    if (incomingTypingTimer) {
      clearTimeout(incomingTypingTimer)
      incomingTypingTimer = null
    }
    if (!payload.typing) {
      clearIncomingTyping()
      return
    }
    incomingTypingPeerUlid.value = payload.peer_ulid
    incomingTypingPeerName.value = payload.peer_name
    incomingTypingChatUlid.value = payload.chat_ulid ?? null
    incomingTypingTimer = setTimeout(() => {
      clearIncomingTyping()
    }, 3500)
  }

  watch(
    () => [
      activeConversationFocus.value?.chat_ulid,
      activeConversationFocus.value?.peer_ulid,
    ],
    () => {
      clearIncomingTyping()
    },
  )

  const activeConversation = computed((): TChatConversation | null => {
    const focus = activeConversationFocus.value
    if (!focus) return null
    if (focus.chat_ulid) {
      const fromList = conversations.value.find(
        (c) => c.chat_ulid === focus.chat_ulid,
      )
      if (fromList) return fromList
    }
    if (focus.type === 'direct' && focus.peer_ulid) {
      const fromList = conversations.value.find(
        (c) => c.type === 'direct' && c.peer_ulid === focus.peer_ulid,
      )
      if (fromList) return fromList
      return {
        chat_ulid: focus.chat_ulid ?? '',
        type: 'direct',
        name: focus.display_name,
        peer_ulid: focus.peer_ulid,
        peer_name: focus.display_name,
        avatar: null,
        group_avatars: null,
        last_message_body: '',
        last_message_at: null,
        unread_count: 0,
        member_count: 2,
      }
    }
    if (focus.type === 'group' && focus.chat_ulid) {
      return {
        chat_ulid: focus.chat_ulid,
        type: 'group',
        name: focus.display_name,
        peer_ulid: null,
        peer_name: null,
        avatar: null,
        group_avatars: null,
        last_message_body: '',
        last_message_at: null,
        unread_count: 0,
        member_count: 0,
      }
    }
    return null
  })

  const isThreadVisible = (chatUlid: string): boolean =>
    conversationDrawerVisible.value &&
    activeConversationFocus.value?.chat_ulid === chatUlid

  const isDirectPeerVisible = (peerUlid: string): boolean =>
    conversationDrawerVisible.value &&
    activeConversationFocus.value?.type === 'direct' &&
    activeConversationFocus.value?.peer_ulid === peerUlid

  const resolveThreadKey = (message: TChatMessage): string | null => {
    if (message.chat_ulid) return message.chat_ulid
    if (!meUlid.value || !message.sender_ulid) return null
    if (message.chat_type === 'group' || !message.recipient_ulid) {
      return null
    }
    if (message.sender_ulid === meUlid.value) return message.recipient_ulid
    if (message.recipient_ulid === meUlid.value) return message.sender_ulid
    return null
  }

  const resolvePeerUlid = (message: TChatMessage): string | null => {
    if (message.chat_type === 'group' || !message.recipient_ulid) return null
    if (!meUlid.value) return null
    if (message.sender_ulid === meUlid.value) return message.recipient_ulid
    if (message.recipient_ulid === meUlid.value) return message.sender_ulid
    return null
  }

  const extractMessageFromRealtimeEvent = (
    event: unknown,
  ): TChatMessage | null => {
    if (!event) return null

    const asRecord = event as Record<string, unknown>
    const direct = asRecord.message as TChatMessage | undefined
    if (direct?.ulid && direct.sender_ulid) return direct

    const data = asRecord.data
    if (
      data &&
      typeof data === 'object' &&
      !Array.isArray(data) &&
      'message' in data
    ) {
      const msg = (data as { message?: TChatMessage }).message
      if (msg?.ulid && msg.sender_ulid) return msg
    }

    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data) as { message?: TChatMessage }
        const msg = parsed?.message
        if (msg?.ulid && msg.sender_ulid) return msg
      } catch {
        return null
      }
    }

    return null
  }

  const applyConversationsFromQuery = (rows: TChatConversation[]) => {
    conversations.value = sortConversationsByLastMessage(rows)
  }

  const applySentMessage = (message: TChatMessage) => {
    const peer = resolvePeerUlid(message)
    if (message.chat_ulid && peer && messagesByThread.value[peer]) {
      const old = messagesByThread.value[peer]
      delete messagesByThread.value[peer]
      messagesByThread.value[message.chat_ulid] = sortConversation(old)
    }
    if (
      message.chat_ulid &&
      activeConversationFocus.value &&
      !activeConversationFocus.value.chat_ulid
    ) {
      activeConversationFocus.value = {
        ...activeConversationFocus.value,
        chat_ulid: message.chat_ulid,
      }
    }
    upsertMessage(message)
    upsertConversationFromMessage(message)
  }

  const syncMessagesFromQuery = (
    chatUlid: string,
    messages: TChatMessage[],
  ) => {
    messagesByThread.value[chatUlid] = sortConversation(messages)
  }

  const upsertConversationFromMessage = (message: TChatMessage) => {
    const chatUlid = message.chat_ulid
    const peerUlid = resolvePeerUlid(message)
    const fromOther = message.sender_ulid !== meUlid.value

    if (chatUlid) {
      const existing = conversations.value.find((c) => c.chat_ulid === chatUlid)
      const label =
        message.chat_type === 'group'
          ? (existing?.name ?? 'Grupo')
          : (existing?.name ??
            (fromOther ? message.sender_name : message.recipient_name) ??
            'Usuario')

      if (existing) {
        existing.last_message_body = chatMessagePreviewLine(message)
        existing.last_message_at = message.created_at
        if (message.chat_type !== 'group') {
          const peerAv = fromOther
            ? message.sender_avatar
            : message.recipient_avatar
          if (peerAv) {
            existing.avatar = peerAv
          }
        }
        if (fromOther && !isThreadVisible(chatUlid)) {
          existing.unread_count = (existing.unread_count ?? 0) + 1
        }
      } else {
        conversations.value.push({
          chat_ulid: chatUlid,
          type: message.chat_type === 'group' ? 'group' : 'direct',
          name: label,
          peer_ulid: peerUlid ?? null,
          peer_name: peerUlid
            ? ((fromOther ? message.sender_name : message.recipient_name) ??
              null)
            : null,
          avatar:
            message.chat_type === 'group'
              ? null
              : ((fromOther
                  ? message.sender_avatar
                  : message.recipient_avatar) ?? null),
          group_avatars: null,
          last_message_body: chatMessagePreviewLine(message),
          last_message_at: message.created_at,
          unread_count: fromOther && !isThreadVisible(chatUlid) ? 1 : 0,
          member_count: message.chat_type === 'group' ? 0 : 2,
        })
      }
      conversations.value = sortConversationsByLastMessage(conversations.value)
      return
    }

    if (!peerUlid) return

    const fallbackName = fromOther
      ? message.sender_name
      : message.recipient_name
    const existing = conversations.value.find(
      (c) => c.type === 'direct' && c.peer_ulid === peerUlid,
    )
    if (existing && message.chat_ulid) {
      existing.chat_ulid = message.chat_ulid
    }
    if (existing) {
      existing.last_message_body = chatMessagePreviewLine(message)
      existing.last_message_at = message.created_at
      const peerAv = fromOther
        ? message.sender_avatar
        : message.recipient_avatar
      if (peerAv) {
        existing.avatar = peerAv
      }
      if (fromOther && !isDirectPeerVisible(peerUlid)) {
        existing.unread_count = (existing.unread_count ?? 0) + 1
      }
    } else {
      conversations.value.push({
        chat_ulid: message.chat_ulid ?? '',
        type: 'direct',
        name: fallbackName || 'Usuario',
        peer_ulid: peerUlid,
        peer_name: fallbackName || 'Usuario',
        avatar:
          (fromOther ? message.sender_avatar : message.recipient_avatar) ??
          null,
        group_avatars: null,
        last_message_body: chatMessagePreviewLine(message),
        last_message_at: message.created_at,
        unread_count: fromOther && !isDirectPeerVisible(peerUlid) ? 1 : 0,
        member_count: 2,
      })
    }

    conversations.value = sortConversationsByLastMessage(conversations.value)
  }

  const upsertMessage = (
    message: TChatMessage,
    options?: { fromIncoming?: boolean },
  ) => {
    const key = message.chat_ulid ?? resolveThreadKey(message)
    if (!key) return

    const current = messagesByThread.value[key] ?? []
    const exists = current.some((item) => item.ulid === message.ulid)
    if (!exists) {
      messagesByThread.value[key] = sortConversation([...current, message])
    }

    if (options?.fromIncoming) {
      upsertConversationFromMessage(message)
    }
  }

  const openListDrawer = async () => {
    unlockChatAudio()
    listDrawerVisible.value = true
  }

  const closeListDrawer = () => {
    listDrawerVisible.value = false
  }

  const toggleListDrawer = async () => {
    if (listDrawerVisible.value) {
      closeListDrawer()
      return
    }
    await openListDrawer()
  }

  const openConversation = (payload: {
    type: 'direct' | 'group'
    chat_ulid?: string
    peer_ulid?: string
    display_name: string
  }) => {
    unlockChatAudio()
    activeConversationFocus.value = {
      type: payload.type,
      chat_ulid: payload.chat_ulid,
      peer_ulid: payload.peer_ulid,
      display_name: payload.display_name,
    }
    conversationDrawerVisible.value = true
    listDrawerVisible.value = true
  }

  const closeConversationDrawer = () => {
    conversationDrawerVisible.value = false
    activeConversationFocus.value = null
  }

  const initialize = async () => {
    if (isInitialized.value || !auth.user?.ulid) return

    const tenantUlid = auth.user.tenant?.ulid
    if (!tenantUlid) {
      if (import.meta.env.DEV) {
        console.warn(
          '[chat] Falta tenant.ulid en el usuario; no se inicializa Echo.',
        )
      }
      return
    }

    await ensureSanctumCsrfCookie()

    const echo = createEchoClient()
    const presenceChannel = `chat.presence.${tenantUlid}`
    presenceChannelJoined.value = presenceChannel
    privateUserChannelUlid.value = auth.user.ulid

    echo
      .join(presenceChannel)
      .here((users: Array<{ ulid: string }>) => {
        const map: Record<string, true> = {}
        for (const user of users) {
          if (user?.ulid) {
            map[user.ulid] = true
          }
        }
        onlineUlids.value = map
      })
      .joining((user: { ulid: string }) => {
        if (!user?.ulid) return
        onlineUlids.value = { ...onlineUlids.value, [user.ulid]: true }
      })
      .leaving((user: { ulid: string }) => {
        if (!user?.ulid) return
        const next = { ...onlineUlids.value }
        delete next[user.ulid]
        onlineUlids.value = next
      })

    echo
      .private(`user.${auth.user.ulid}`)
      .listen('.chat.message.sent', async (event: unknown) => {
        const message = extractMessageFromRealtimeEvent(event)
        if (!message) return

        const chatUlid = message.chat_ulid
        upsertMessage(message, { fromIncoming: true })

        if (
          chatUlid &&
          chatUlid === activeChatUlid.value &&
          message.sender_ulid !== auth.user?.ulid
        ) {
          clearIncomingTyping()
        }

        const peer = resolvePeerUlid(message)
        void queryClient.invalidateQueries({
          queryKey: chatKeys.conversations(),
        })
        if (chatUlid) {
          void queryClient.invalidateQueries({
            queryKey: chatKeys.messages(chatUlid),
          })
        } else if (peer) {
          void queryClient.invalidateQueries({
            queryKey: chatKeys.messages(peer),
          })
        }

        if (message.sender_ulid !== auth.user?.ulid) {
          playIncomingMessageSound()
          const visible =
            (chatUlid && isThreadVisible(chatUlid)) ||
            (peer && isDirectPeerVisible(peer))
          if (!visible) {
            showChatIncomingToast({
              peerUlid: peer ?? '',
              chat_ulid: chatUlid,
              type: message.chat_type === 'group' ? 'group' : 'direct',
              senderName: message.sender_name?.trim() || 'Usuario',
              body: message.body,
              list_preview: message.list_preview,
            })
          }
        }
      })
      .listen('.chat.user.typing', (raw: unknown) => {
        const payload = raw as TChatUserTypingPayload
        if (!payload?.peer_ulid) return
        applyIncomingTypingPayload(payload)
      })

    isInitialized.value = true
  }

  const reset = () => {
    const echo = getEchoClient()
    if (echo) {
      const privateUlid = privateUserChannelUlid.value ?? auth.user?.ulid
      if (privateUlid) {
        echo.leave(`user.${privateUlid}`)
      }
      if (presenceChannelJoined.value) {
        echo.leave(presenceChannelJoined.value)
      }
    }
    presenceChannelJoined.value = null
    privateUserChannelUlid.value = null
    disconnectEchoClient()

    queryClient.removeQueries({ queryKey: chatKeys.all })

    listDrawerVisible.value = false
    conversationDrawerVisible.value = false
    activeConversationFocus.value = null
    conversations.value = []
    messagesByThread.value = {}
    onlineUlids.value = {}
    clearIncomingTyping()
    isInitialized.value = false
  }

  return {
    activeChatUlid,
    activeConversation,
    activeConversationFocus,
    activeIsGroup,
    activeMessages,
    activePeerUlid,
    applyConversationsFromQuery,
    applySentMessage,
    closeConversationDrawer,
    closeListDrawer,
    conversationDrawerVisible,
    conversations,
    hasPendingMessages,
    initialize,
    isInitialized,
    listDrawerVisible,
    messagesByThread,
    onlineUlids,
    openConversation,
    openListDrawer,
    peerTypingDisplayName,
    reset,
    showPeerIsTyping,
    syncMessagesFromQuery,
    toggleListDrawer,
    totalUnreadCount,
  }
})
